// src/pages/AdminAnalytics.jsx
import React, { useEffect, useState } from "react";
import {
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import {
  TrendingUp,
  Loader2,
  PieChart as PieIcon,
  LineChart as LineIcon,
  BarChart3,
  DollarSign,
  ListOrdered,
  CheckCircle,
  Activity,
  Zap,
  Target,
} from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { superAdminAPI } from "../services/api";
import DashboardNavbar from "../components/Navbar";

const COLORS = ["#007bff", "#28a745", "#ffc107", "#17a2b8", "#dc3545"];

const SuperAdminAnalytics = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const { data } = await superAdminAPI.getAnalytics();
      setOrders(data.allOrders || []);
    } catch (error) {
      console.error("Analytics Load Error:", error);
    } finally {
      setLoading(false);
    }
  };

 if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 text-secondary">
        <Loader2 className="me-2 spinner-border text-primary" />
        Loading Analytics...
      </div>
    );

  // === Stats ===
  const totalIncome = orders.reduce((sum, o) => sum + o.totalPrice, 0);
  const totalOrders = orders.length;
  const deliveredOrders = orders.filter((o) => o.isDelivered).length;

  // === Monthly trend data ===
  const monthlyData = orders.reduce((acc, order) => {
    const month = new Date(order.createdAt).toLocaleString("default", {
      month: "short",
    });
    acc[month] = (acc[month] || 0) + order.totalPrice;
    return acc;
  }, {});
  const chartData = Object.entries(monthlyData).map(([month, total]) => ({
    month,
    total,
  }));

  // === Weekly data for pie charts ===
  const getWeekNumber = (date) => {
    const d = new Date(date);
    const firstDay = new Date(d.getFullYear(), 0, 1);
    const diff =
      d - firstDay + (firstDay.getTimezoneOffset() - d.getTimezoneOffset()) * 60000;
    return Math.floor(diff / (7 * 24 * 60 * 60 * 1000)) + 1;
  };

  const weeklyData = {};
  orders.forEach((o) => {
    const week = `Week ${getWeekNumber(o.createdAt)}`;
    if (!weeklyData[week]) weeklyData[week] = { income: 0, orders: 0 };
    weeklyData[week].income += o.totalPrice;
    weeklyData[week].orders += 1;
  });

  const weeklyIncomeData = Object.entries(weeklyData).map(([week, data]) => ({
    name: week,
    value: data.income,
  }));
  const weeklyOrderData = Object.entries(weeklyData).map(([week, data]) => ({
    name: week,
    value: data.orders,
  }));

  return (
    <>
      <DashboardNavbar />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={true} closeOnClick={true} />
      <div className="container-fluid py-4" style={{backgroundColor: "#f8f9fa", minHeight: "100vh"}}>
        {/* HEADER */}
        <div className="mb-5 px-4">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="p-3 bg-primary bg-opacity-10 rounded-3">
              <BarChart3 size={32} className="text-primary" />
            </div>
            <div>
              <h2 className="fw-bold mb-0 text-dark">Super Admin Analytics Dashboard</h2>
              <p className="text-muted small mb-0">Real-time insights and performance metrics</p>
            </div>
          </div>
        </div>

        {/* === MAIN KPI CARDS === */}
        <div className="row g-4 mb-5 px-4">
          {/* Total Income */}
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", overflow: "hidden", position: "relative"}}>
              <div className="position-absolute" style={{right: "-30px", top: "-30px", opacity: 0.1}}>
                <DollarSign size={120} />
              </div>
              <div className="card-body position-relative d-flex flex-column justify-content-between">
                <div>
                  <p className="opacity-90 small mb-2 d-flex align-items-center gap-2">
                    <DollarSign size={16} />
                    Total Income
                  </p>
                  <h3 className="fw-bold mb-0">₹{totalIncome.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Total Orders */}
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", color: "white", overflow: "hidden", position: "relative"}}>
              <div className="position-absolute" style={{right: "-30px", top: "-30px", opacity: 0.1}}>
                <ListOrdered size={120} />
              </div>
              <div className="card-body position-relative d-flex flex-column justify-content-between">
                <div>
                  <p className="opacity-90 small mb-2 d-flex align-items-center gap-2">
                    <ListOrdered size={16} />
                    Total Orders
                  </p>
                  <h3 className="fw-bold mb-0">{totalOrders}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Delivered Orders */}
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)", color: "white", overflow: "hidden", position: "relative"}}>
              <div className="position-absolute" style={{right: "-30px", top: "-30px", opacity: 0.1}}>
                <CheckCircle size={120} />
              </div>
              <div className="card-body position-relative d-flex flex-column justify-content-between">
                <div>
                  <p className="opacity-90 small mb-2 d-flex align-items-center gap-2">
                    <CheckCircle size={16} />
                    Delivered
                  </p>
                  <h3 className="fw-bold mb-0">{deliveredOrders}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Rate */}
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm h-100" style={{background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)", color: "white", overflow: "hidden", position: "relative"}}>
              <div className="position-absolute" style={{right: "-30px", top: "-30px", opacity: 0.1}}>
                <TrendingUp size={120} />
              </div>
              <div className="card-body position-relative d-flex flex-column justify-content-between">
                <div>
                  <p className="opacity-90 small mb-2 d-flex align-items-center gap-2">
                    <Activity size={16} />
                    Delivery Rate
                  </p>
                  <h3 className="fw-bold mb-0">{((deliveredOrders / totalOrders) * 100).toFixed(1)}%</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === SECONDARY STATS === */}
        <div className="row g-4 mb-5 px-4">
          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm bg-light">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-muted small mb-2">Avg Order Value</p>
                    <h5 className="fw-bold mb-0">₹{(totalIncome / totalOrders).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</h5>
                  </div>
                  <Target size={40} className="text-primary opacity-25" />
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm bg-light">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-muted small mb-2">Pending Orders</p>
                    <h5 className="fw-bold mb-0">{totalOrders - deliveredOrders}</h5>
                  </div>
                  <Zap size={40} className="text-warning opacity-25" />
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm bg-light">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-muted small mb-2">Paid Orders</p>
                    <h5 className="fw-bold mb-0">{orders.filter((o) => o.isPaid).length}</h5>
                  </div>
                  <DollarSign size={40} className="text-success opacity-25" />
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <div className="card border-0 shadow-sm bg-light">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="text-muted small mb-2">Payment Rate</p>
                    <h5 className="fw-bold mb-0">{((orders.filter((o) => o.isPaid).length / totalOrders) * 100).toFixed(1)}%</h5>
                  </div>
                  <CheckCircle size={40} className="text-info opacity-25" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === CHARTS SECTION === */}
        <div className="row g-4 px-4 mb-5">
          {/* Monthly Revenue Area Chart */}
          <div className="col-lg-12">
            <div className="card shadow-sm border-0 p-4" style={{borderRadius: "12px"}}>
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="p-2 bg-primary bg-opacity-10 rounded-2">
                  <LineIcon size={20} className="text-primary" />
                </div>
                <h5 className="fw-bold mb-0 flex-grow-1">Monthly Revenue Trend</h5>
                <span className="badge bg-success">Growing</span>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#667eea" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="month" stroke="#888" />
                  <YAxis stroke="#888" />
                  <Tooltip contentStyle={{backgroundColor: "#f8f9fa", border: "1px solid #ddd", borderRadius: "8px"}} formatter={(value) => `₹${value.toLocaleString("en-IN")}`} />
                  <Area type="monotone" dataKey="total" stroke="#667eea" fillOpacity={1} fill="url(#colorTotal)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Weekly Income Distribution Donut Chart */}
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 p-4" style={{borderRadius: "12px"}}>
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="p-2 bg-success bg-opacity-10 rounded-2">
                  <PieIcon size={20} className="text-success" />
                </div>
                <h5 className="fw-bold mb-0">Weekly Income Distribution</h5>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={weeklyIncomeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    label={{position: "outside", fill: "#666", fontSize: 12}}
                  >
                    {weeklyIncomeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `₹${value.toLocaleString("en-IN")}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Weekly Orders Bar Chart */}
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 p-4" style={{borderRadius: "12px"}}>
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="p-2 bg-warning bg-opacity-10 rounded-2">
                  <BarChart3 size={20} className="text-warning" />
                </div>
                <h5 className="fw-bold mb-0">Weekly Orders Count</h5>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={weeklyOrderData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="name" stroke="#888" />
                  <YAxis stroke="#888" />
                  <Tooltip contentStyle={{backgroundColor: "#f8f9fa", border: "1px solid #ddd", borderRadius: "8px"}} />
                  <Bar dataKey="value" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdminAnalytics;
